import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { fmtMoney } from "@/lib/invoicing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, CreditCard, FileText, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicInvoicePayPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [payState, setPayState] = useState(sessionId ? "checking" : "");
  const [customAmount, setCustomAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/public/invoices/${token}`);
      setInv(data);
    } catch (e) {
      setError("Invoice not found or no longer available.");
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      if (attempts++ >= 8) { setPayState("timeout"); return; }
      try {
        const { data } = await axios.get(
          `${API}/public/invoices/${token}/checkout/${sessionId}/status`);
        if (data.payment_status === "paid") { setPayState("paid"); load(); return; }
        if (data.status === "expired") { setPayState("expired"); return; }
        setTimeout(poll, 2000);
      } catch { setPayState("error"); }
    };
    poll();
  }, [sessionId, token, load]);

  const checkout = async (amount) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/invoices/${token}/checkout`, {
        origin_url: window.location.origin,
        amount: amount || null,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start payment.");
      setBusy(false);
    }
  };

  if (error && !inv) {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-50 text-zinc-500">{error}</div>;
  }
  if (!inv) {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-50">
      <Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;
  }
  const paid = inv.status === "paid";

  return (
    <div className="min-h-screen bg-zinc-100 py-10 px-4" data-testid="public-pay-page">
      <div className="max-w-2xl mx-auto space-y-4">
        {payState === "paid" && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3 text-emerald-800"
            data-testid="payment-success-banner">
            <CheckCircle2 className="h-6 w-6" />
            <div><b>Payment successful.</b> A receipt has been emailed to you. Thank you!</div>
          </div>
        )}
        {payState === "checking" && (
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 flex items-center gap-3 text-blue-800">
            <Loader2 className="h-5 w-5 animate-spin" /> Confirming your payment…
          </div>
        )}
        {["expired", "timeout", "error"].includes(payState) && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-amber-800">
            We couldn't confirm the payment yet. If you completed payment it will be
            reflected shortly — check your email for a receipt.
          </div>
        )}
        {error && <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>}

        <div className="bg-white rounded-xl shadow-sm border p-6 space-y-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-zinc-400">Tax invoice</div>
              <h1 className="text-2xl font-bold text-[#002FA7]">{inv.invoice_number}</h1>
              <div className="text-sm text-zinc-500">{inv.entity_name}</div>
            </div>
            <div className="text-right text-sm">
              <Badge variant="outline" className={paid
                ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                : "bg-blue-50 text-blue-800 border-blue-200"}>
                {paid ? "PAID" : inv.status === "partially_paid" ? "PART PAID" : "AWAITING PAYMENT"}
              </Badge>
              <div className="text-zinc-500 mt-1">Issued {inv.issue_date}</div>
              <div className="text-zinc-500">Due {inv.due_date}</div>
            </div>
          </div>

          <div className="text-sm"><span className="text-zinc-500">Billed to </span>
            <b>{inv.client_name}</b></div>

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-zinc-500">
                <th className="py-2">Description</th><th className="py-2 text-right">Qty</th>
                <th className="py-2 text-right">Unit</th><th className="py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {inv.line_items.map((li, i) => (
                <tr key={li.line_item_id || `${li.description}-${i}`} className="border-b">
                  <td className="py-2">{li.description}
                    {!li.gst && <span className="text-xs text-zinc-400"> (GST free)</span>}</td>
                  <td className="py-2 text-right">{li.quantity}</td>
                  <td className="py-2 text-right">{fmtMoney(li.unit_price)}</td>
                  <td className="py-2 text-right">{fmtMoney(li.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-end">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between text-zinc-500"><span>Subtotal</span><span>{fmtMoney(inv.subtotal)}</span></div>
              <div className="flex justify-between text-zinc-500"><span>GST 10%</span><span>{fmtMoney(inv.gst_total)}</span></div>
              <div className="flex justify-between font-semibold"><span>Total</span><span>{fmtMoney(inv.total)}</span></div>
              {inv.amount_paid > 0 && (
                <div className="flex justify-between text-emerald-700">
                  <span>Payments received</span><span>-{fmtMoney(inv.amount_paid)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg border-t pt-1">
                <span>Balance due</span>
                <span data-testid="public-balance-due">{fmtMoney(inv.balance_due)}</span>
              </div>
            </div>
          </div>

          {!paid && inv.stripe_enabled && (
            <div className="border-t pt-5 space-y-3">
              <Button className="w-full bg-[#002FA7] hover:bg-[#00246e]" size="lg"
                disabled={busy} onClick={() => checkout(null)} data-testid="pay-full-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
                Pay {fmtMoney(inv.balance_due)} now
              </Button>
              <div className="flex gap-2 items-center">
                <Input type="number" step="0.01" min="1" max={inv.balance_due}
                  placeholder="Or enter a part-payment amount" value={customAmount}
                  data-testid="custom-amount-input"
                  onChange={(e) => setCustomAmount(e.target.value)} />
                <Button variant="outline" disabled={busy || !Number(customAmount)}
                  data-testid="pay-custom-btn"
                  onClick={() => checkout(Number(customAmount))}>
                  Pay part
                </Button>
              </div>
              <div className="text-xs text-zinc-400 text-center">
                Secure card payment powered by Stripe · AUD
              </div>
            </div>
          )}

          {!paid && inv.bank_details && (
            <div className="border-t pt-4 text-sm">
              <div className="font-semibold mb-1">Pay by direct deposit</div>
              <pre className="whitespace-pre-wrap font-sans text-zinc-600 bg-zinc-50 rounded-md p-3 border">
                {inv.bank_details}</pre>
            </div>
          )}

          <div className="border-t pt-4 flex items-center justify-between">
            <a href={`${API}/public/invoices/${token}/pdf`} target="_blank" rel="noreferrer"
              className="text-sm text-[#002FA7] underline flex items-center gap-1"
              data-testid="public-pdf-link">
              <FileText className="h-4 w-4" /> Download PDF
            </a>
            {inv.footer_text && <span className="text-xs text-zinc-400">{inv.footer_text}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
